import React, { useCallback } from 'react';

import { ActionIcon, createStyles, Group, Paper, Text, Title, Tooltip } from '@mantine/core';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { useMediaQuery } from '@mantine/hooks';
import { IconFilterOff } from '@tabler/icons-react';
import BackButton from 'components/BackButton';
import DownloadCSV from 'components/DownloadCSV';
import AthleteStatusMultiSelect from 'components/form-fields/select-fields/AthleteStatusMultiSelect';
import HelpButton from 'components/HelpButton';
import dayjs from 'dayjs';
import useAthleteBirthdays from 'hooks/reports/athletes/useAthleteBirthdays';
import { useNavigate } from 'react-router-dom';
import { AthleteBirthdayReport } from 'types';
import { setDateDifference } from 'utils/date-functions';

import { SearchFilters } from '../types';

const useStyles = createStyles(theme => ({
  header: {
    width: '100%',
    background: theme.colors.gray[0],
    padding: theme.spacing.xl,
    paddingBottom: theme.spacing.xl,
    display: 'grid',
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: 0,
    gridTemplateColumns: 'min-content auto max-content max-content min-content min-content',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `"backButton title dateRange statuses clear download"`,
    alignItems: 'center',
    gap: theme.spacing.sm,
    [theme.fn.smallerThan('md')]: {
      gridTemplateColumns: 'min-content auto auto min-content min-content',
      gridTemplateAreas: `
        'backButton title title clear download'
        'dateRange dateRange statuses statuses statuses'
        `,
    },
    [theme.fn.smallerThan('sm')]: {
      padding: theme.spacing.sm,
      gridTemplateColumns: 'min-content auto min-content',
      gridTemplateAreas: `
        'backButton title clear'
        'dateRange dateRange dateRange'
        'statuses statuses statuses'
        `,
    },
  },
  backButton: {
    gridArea: 'backButton',
  },
  title: {
    gridArea: 'title',
  },
  dateRange: {
    gridArea: 'dateRange',
    minWidth: '15rem',
  },
  statuses: {
    gridArea: 'statuses',
    minWidth: '15rem',
    maxWidth: '30rem',
  },
  clear: {
    gridArea: 'clear',
  },
  download: {
    gridArea: 'download',
    [theme.fn.smallerThan('sm')]: {
      display: 'none',
    },
  },
}));

const csvOptions = {
  fieldSeparator: ',',
  quoteStrings: '',
  decimalSeparator: '.',
  showLabels: true,
  useBom: true,
  useKeysAsHeaders: true,
};

const dataToRow = (d: AthleteBirthdayReport): { [key: string]: any } => ({
  'Full Name': d.fullName,
  'Current Status': d.athleteStatus,
  Email: d.email,
  Phone: d.phone,
  'Date of Birth': dayjs(d.dob).format('YYYY-MM-DD'),
});

const AthleteBirthdaysPageHeader = ({
  initialSearchFiltersValue,
  searchFilters,
  setSearchFilters,
  orderingValue,
}: {
  initialSearchFiltersValue: SearchFilters;
  searchFilters: SearchFilters;
  setSearchFilters: React.Dispatch<React.SetStateAction<SearchFilters>>;
  orderingValue: string;
}): JSX.Element => {
  const { classes, theme } = useStyles();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);

  const navigate = useNavigate();

  const handleBackClick = useCallback((): void => {
    navigate(-1);
  }, [navigate]);

  const handleDateChange = (value: DatesRangeValue): void => {
    setSearchFilters(prev => ({ ...prev, dateRange: value }));
  };

  const handleStatusChange = (value: string[]): void => {
    setSearchFilters(prev => ({ ...prev, athleteStatuses: value }));
  };

  const handleClearFilters = (): void => {
    setSearchFilters(initialSearchFiltersValue);
  };

  return (
    <Paper className={classes.header}>
      <BackButton handleOnClick={handleBackClick} />
      <Group noWrap spacing='xl' className={classes.title} data-cy='athleteBirthdaysPageHeader'>
        <Title order={isDesktop ? 1 : 3}>Athlete Birthdays</Title>
        <HelpButton width={400} position={isDesktop ? 'right' : 'bottom'}>
          <Text size='sm'>{'Athletes with a birthday within the selected date range.'}</Text>
        </HelpButton>
      </Group>
      <DatePickerInput
        className={classes.dateRange}
        value={searchFilters.dateRange}
        onChange={handleDateChange}
        valueFormat='MMMM DD, YYYY'
        type='range'
        allowSingleDateInRange
        maxDate={setDateDifference(searchFilters.dateRange[0] || new Date(), 1, 0, -1)}
        maxLevel='month'
        required
        {...{ placeholder: 'Select date range' }}
      />
      <AthleteStatusMultiSelect
        className={classes.statuses}
        placeholder='Select statuses'
        onChange={handleStatusChange}
        value={searchFilters.athleteStatuses}
        clearable
      />
      <Tooltip label='Clear filters' className={classes.clear}>
        <ActionIcon onClick={handleClearFilters}>
          <IconFilterOff />
        </ActionIcon>
      </Tooltip>
      <DownloadCSV
        useList={useAthleteBirthdays}
        dataToRow={dataToRow}
        csvOptions={csvOptions}
        hookOptions={{
          dateStart: searchFilters.dateRange[0]
            ? dayjs(searchFilters.dateRange[0]).format('YYYY-MM-DD')
            : undefined,
          dateEnd: searchFilters.dateRange[1]
            ? dayjs(searchFilters.dateRange[1]).format('YYYY-MM-DD')
            : undefined,
          athleteStatuses: searchFilters.athleteStatuses,
          ordering: orderingValue,
        }}
        className={classes.download}
        reportType='athlete-birthdays'
        startDate={
          searchFilters.dateRange[0] ? dayjs(searchFilters.dateRange[0]).format('YYYY-MM-DD') : null
        }
        endDate={searchFilters.dateRange[1] ? dayjs(searchFilters.dateRange[1]).format('YYYY-MM-DD') : null}
      />
    </Paper>
  );
};

export default AthleteBirthdaysPageHeader;
